// components/CreateNewGroupForm/index.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Badge, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, ListGroup, ListGroupItem, Row } from 'reactstrap';
import { Btn, H5 } from '../../../../AbstractElements';
import { useCreateGroup } from '../../../../hooks/useCreateGroup';
import { useManageGroup } from '../../../../hooks/useManageGroup';
import { GroupCreateType } from '../../../../Types/Group.type';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setGroupDetails } from '../../../../ReduxToolkit/GroupReducers/GroupReducer';
import { useFetchGroupLeaders } from '../../../../hooks/useFetchGroupLeaders';
import { useFetchGroups } from '../../../../hooks/useFetchGroups';
import { useFetchParticipants, Participant } from '../../../../hooks/useFetchParticipants';
import Breadcrumbs from '../../../../CommonElements/Breadcrumbs';

interface CreateNewGroupFormProps {
  groupData?: GroupCreateType; // Optional prop for editing
  onCancel?: () => void;       // Optional handler for modal cancel
  onUpdate?: () => void; // callback after update
}

export default function CreateNewGroupForm({ onUpdate }: CreateNewGroupFormProps) {
  const group = useSelector((state: any) => state?.group?.editGroupDetails);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();


  const user = JSON.parse(localStorage.getItem('user') || '{}') as { id?: string };
  const { refetch: refetchGroups } = useFetchGroups(user?.id || '');
  if (location.pathname.includes('/group/create_new')) {
    dispatch(setGroupDetails(null));
  }

  const normalizedDate = group?.date
    ? new Date(group.date).toISOString().split('T')[0]
    : '';

  const { handleCreateGroup, loading: creating } = useCreateGroup();
  const { handleManageGroup, loading: updating } = useManageGroup();

  const { leaders: groupLeaders, loading: leadersLoading } = useFetchGroupLeaders();
  const { participants, loading: participantsLoading } = useFetchParticipants();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<GroupCreateType>({
    mode: 'onChange',
    defaultValues: useMemo(() => {
      // normalize participants to array of ids (handle strings or objects)
      const groupParticipantIds: string[] = Array.isArray((group as any)?.participants)
        ? (group as any).participants.map((p: any) => (typeof p === 'string' ? p : String(p._id ?? p.id ?? ''))).filter(Boolean)
        : [];

      return {
        ...group,
        date: normalizedDate,
        groupLeader: (group as any)?.groupLeader ?? '',
        participants: groupParticipantIds
      };
    }, [group, normalizedDate])
  });

  // default groupLeader to first if none selected
  useEffect(() => {
    const current = (group as any)?.groupLeader || '';
    if (current) return;
    if (Array.isArray(groupLeaders) && groupLeaders.length > 0) {
      const first = groupLeaders[0] as any;
      const firstId = first?.id ?? first?._id ?? '';
      if (firstId) {
        setValue('groupLeader', String(firstId));
      }
    }
  }, [groupLeaders, group, setValue]);

  const formParticipants: string[] = watch('participants') ?? [];
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // derive visible participant search results
  const availableParticipants = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    return (participants || [])
      .filter(p => {
        const id = String(p.id ?? p._id ?? '');
        if (!id) return false;
        if (formParticipants.includes(id)) return false;
        if (!term) return true;
        const name = `${p.firstname ?? ''} ${p.lastname ?? ''}`.toLowerCase();
        const email = (p.email ?? '').toLowerCase();
        return name.includes(term) || email.includes(term);
      })
      .slice(0, 100);
  }, [participants, formParticipants, searchTerm]);

  const addParticipant = (p: Participant) => {
    const id = String(p.id ?? p._id ?? '');
    if (!id) return;
    const next = Array.from(new Set([...(formParticipants || []), id]));
    setValue('participants', next, { shouldDirty: true, shouldTouch: true });
    setSearchTerm('');
    setDropdownOpen(false);
  };

  const removeParticipant = (idToRemove: string) => {
    const next = (formParticipants || []).filter(id => id !== idToRemove);
    setValue('participants', next, { shouldDirty: true, shouldTouch: true });
  };

  const onSubmit: SubmitHandler<GroupCreateType> = (data) => {
    if (group) {
      handleManageGroup(group?._id || '', data);
      navigate(`${process.env.PUBLIC_URL}/group/group_list`);
      dispatch(setGroupDetails(null));
    } else {
      handleCreateGroup(data);
      navigate(`${process.env.PUBLIC_URL}/group/group_list`);
      refetchGroups();
    }
  };

  const initialsOf = (p?: Participant) => {
    const a = (p?.firstname ?? '').trim();
    const b = (p?.lastname ?? '').trim();
    if (a || b) return `${(a[0] ?? '').toUpperCase()}${(b[0] ?? '').toUpperCase()}`.trim();
    const e = p?.email ?? '';
    return (e[0] ?? '').toUpperCase();
  };

  // -------- theme-adaptive colors (read from computed styles to follow light/dark toggle) --------
  const [themeColors, setThemeColors] = useState({
    bg: '#0b0f10',
    text: '#e6eef8',
    subtle: 'rgba(255,255,255,0.06)',
    muted: '#98a0a6',
    pillBg: '#0e1112',
  });

  useEffect(() => {
    try {
      const cs = getComputedStyle(document.body);
      const bodyBg = cs.getPropertyValue('background-color') || cs.backgroundColor || '';
      const bodyColor = cs.getPropertyValue('color') || cs.color || '';
      // parse rgb(a) string "rgb(r,g,b)" or "rgba(...)" into numbers
      const parseRgb = (s: string) => {
        const m = s.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (!m) return null;
        return [Number(m[1]), Number(m[2]), Number(m[3])];
      };
      const rgb = parseRgb(bodyBg) || parseRgb(cs.getPropertyValue('--body-bg') || '') || [11, 15, 16];
      const textRgb = parseRgb(bodyColor) || [230, 238, 248];
      const luminance = (rgb: number[]) => {
        const [r, g, b] = rgb.map(v => v / 255).map(v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
      };
      const bgLum = luminance(rgb);
      const isDark = bgLum < 0.5;
      // choose subtle border depending on luminance
      const subtle = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
      const muted = isDark ? '#98a0a6' : '#6b7280';
      const pillBg = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.04)';
      setThemeColors({
        bg: bodyBg || (isDark ? '#0b0f10' : '#fff'),
        text: bodyColor || (isDark ? '#e6eef8' : '#111827'),
        subtle,
        muted,
        pillBg
      });
    } catch (e) {
      // fallback: dark
      setThemeColors({
        bg: '#0b0f10',
        text: '#e6eef8',
        subtle: 'rgba(255,255,255,0.06)',
        muted: '#98a0a6',
        pillBg: '#0e1112',
      });
    }
  }, []);

  // styles that use themeColors
  const styles = {
    pillContainer: {
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap' as const,
      marginTop: 8,
    },
    pill: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      // background: themeColors.pillBg,
      border: `1px solid ${themeColors.subtle}`,
      padding: '6px 8px',
      borderRadius: 20,
      fontSize: 14,
      color: themeColors.text,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    } as React.CSSProperties,
    avatar: {
      width: 28,
      height: 28,
      borderRadius: '50%',
      background: '#2563eb',
      color: '#fff',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      fontSize: 12
    } as React.CSSProperties,
    pillRemoveBtn: {
      cursor: 'pointer',
      padding: '0 6px',
      borderRadius: 12,
      // background: 'transparent',
      border: 'none',
      color: '#ff7b7b',
      fontSize: 16,
      lineHeight: '1',
    } as React.CSSProperties,
    dropdownList: {
      position: 'absolute' as const,
      zIndex: 1200,
      left: 0,
      right: 0,
      maxHeight: 260,
      overflow: 'auto' as const,
      // background: 'black',
      border: `1px solid ${themeColors.subtle}`,
      borderRadius: 8,
      boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
      padding: 6
    } as React.CSSProperties,
    dropdownItem: {
      padding: '10px 12px',
      cursor: 'pointer',
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      borderRadius: 6,
      color: themeColors.text,
      transition: 'background 120ms ease'
    } as React.CSSProperties,
    dropdownSubText: {
      fontSize: 12,
      color: themeColors.muted,
      marginTop: 2
    } as React.CSSProperties
  };

  return (
   

                <Form className="theme-form basic-form" onSubmit={handleSubmit(onSubmit)}>
                  {/* Group Title */}
                  <Row>
                    <Col>
                      <FormGroup>
                        <H5 className="f-w-600 mb-2">Group Title</H5>
                        <Controller
                          name="title"
                          control={control}
                          rules={{ required: 'Group title is required' }}
                          render={({ field }) => (
                            <Input
                              className="form-control"
                              type="text"
                              placeholder="Enter group title"
                              {...field}
                            />
                          )}
                        />
                        {errors.title && (
                          <span style={{ color: 'red' }}>{errors.title.message}</span>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Group Leader (mandatory) + Participants */}
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <H5 className="f-w-600 mb-2">Group Leader</H5>
                        <Controller
                          name="groupLeader"
                          control={control}
                          rules={{ required: 'Group leader is required' }}
                          render={({ field }) => (
                            <Input
                              type="select"
                              className="form-control"
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value)}
                              innerRef={field.ref as any}
                            >
                              <option value="">— Select group leader —</option>
                              {leadersLoading ? (
                                <option value="">Loading leaders...</option>
                              ) : (
                                Array.isArray(groupLeaders) && groupLeaders.map((l: any) => {
                                  const id = l.id ?? l._id;
                                  const label = (l.firstname || l.lastname) ? `${l.firstname ?? ''} ${l.lastname ?? ''}`.trim() : (l.email ?? 'Unnamed');
                                  return <option key={id} value={id}>{label}</option>;
                                })
                              )}
                            </Input>
                          )}
                        />
                        {errors.groupLeader && (
                          <span style={{ color: 'red' }}>{(errors.groupLeader as any)?.message}</span>
                        )}
                      </FormGroup>
                    </Col>

                    {/* <Col md={6}>
          <FormGroup>
            <H5 className="f-w-600 mb-2">Participants</H5>

            <div style={{ position: 'relative' }}>
              <Input
                type="text"
                placeholder={participantsLoading ? 'Loading participants...' : 'Search participants (name or email)'}
                value={searchTerm}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => {
                  // allow click selection
                  setTimeout(() => setDropdownOpen(false), 150);
                }}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setDropdownOpen(true);
                }}
                style={{
                  // background: 'transparent',
                  // color: 'inherit',
                  border: `1px solid ${themeColors.subtle}`
                }}
              />

              {dropdownOpen && availableParticipants.length > 0 && (
                <div style={styles.dropdownList}>
                  {availableParticipants.map((p) => {
                    const id = String(p.id ?? p._id ?? '');
                    const name = `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim() || p.email;
                    return (
                      <div
                        key={id}
                        style={styles.dropdownItem}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => addParticipant(p)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={() => {}}
                      >
                        <div style={{ ...styles.avatar }}>{initialsOf(p)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: themeColors.text }}>{name}</div>
                          <div style={styles.dropdownSubText}>{p.email}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={styles.pillContainer}>
              {(formParticipants || []).map(pid => {
                const p = participants.find(x => String(x.id ?? x._id) === pid);
                const displayName = p ? `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim() || p.email : pid;
                return (
                  <div key={pid} style={styles.pill}>
                    <div style={styles.avatar}>{initialsOf(p)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: themeColors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{displayName}</div>
                      <div style={{ fontSize: 12, color: themeColors.muted }}>{p?.email ?? ''}</div>
                    </div>
                    <button type="button" aria-label={`Remove ${displayName}`} style={styles.pillRemoveBtn} onClick={() => removeParticipant(pid)}>✕</button>
                  </div>
                );
              })}
            </div>


            <Controller
              name="participants"
              control={control}
              render={({ field }) => (
                <input type="hidden" {...field} />
              )}
            />
          </FormGroup>
        </Col> */}

                    <Col md={6}>
                      <FormGroup>
                        <Label className="fw-600 mb-2" for="participantsSearch">Participants</Label>

                        <div className="position-relative">
                          <Input
                            id="participantsSearch"
                            type="text"
                            placeholder={participantsLoading ? 'Loading participants...' : 'Search participants (name or email)'}
                            value={searchTerm}
                            onFocus={() => setDropdownOpen(true)}
                            onBlur={() => {
                              // allow click selection
                              setTimeout(() => setDropdownOpen(false), 150);
                            }}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setDropdownOpen(true);
                            }}
                            // use bootstrap border utility or keep default bootstrap Input look
                            className="border"
                            aria-autocomplete="list"
                            aria-expanded={dropdownOpen}
                          />

                          {dropdownOpen && availableParticipants.length > 0 && (
                            <div
                              className="position-absolute w-100 mt-1 bg-white shadow-sm rounded"
                              style={{ zIndex: 2000, maxHeight: 240, overflow: 'auto' }}
                            >
                              <ListGroup flush>
                                {availableParticipants.map((p) => {
                                  const id = String(p.id ?? p._id ?? '');
                                  const name = `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim() || p.email;
                                  return (
                                    <ListGroupItem
                                      key={id}
                                      tag="button"
                                      action
                                      onMouseDown={(ev) => ev.preventDefault()} // prevent blur before click
                                      onClick={() => addParticipant(p)}
                                      className="d-flex align-items-center"
                                    >
                                      {/* avatar using bootstrap utilities (no custom CSS) */}
                                      <Badge pill className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ width: 36, height: 36 }}>
                                        {initialsOf(p)}
                                      </Badge>

                                      <div className="text-start" style={{ minWidth: 0 }}>
                                        <div className="fw-semibold text-truncate" style={{ maxWidth: '100%' }}>{name}</div>
                                        <small className="text-muted d-block text-truncate">{p.email}</small>
                                      </div>
                                    </ListGroupItem>
                                  );
                                })}
                              </ListGroup>
                            </div>
                          )}
                        </div>

                        {/* Selected participants - pills */}
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {(formParticipants || []).map((pid) => {
                            const p = participants.find((x) => String(x.id ?? x._id) === pid);
                            const displayName = p ? `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim() || p.email : pid;
                            return (
                              <Badge key={pid} color="light" pill className="d-inline-flex align-items-center px-2 py-1 border">
                                <span className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ width: 28, height: 28, fontSize: 12 }}>
                                  {initialsOf(p)}
                                </span>

                                <div className="text-start" style={{ maxWidth: 160 }}>
                                  <div className="text-truncate" style={{ maxWidth: 160 }}>{displayName}</div>
                                  <small className="d-block text-muted">{p?.email ?? ''}</small>
                                </div>

                                <button
                                  type="button"
                                  aria-label={`Remove ${displayName}`}
                                  className="btn btn-sm btn-transparent ms-2"
                                  onClick={() => removeParticipant(pid)}
                                  style={{ border: 'none', background: 'none' }}
                                >
                                  ✕
                                </button>
                              </Badge>
                            );
                          })}
                        </div>

                        {/* hidden controller field for react-hook-form */}
                        <Controller
                          name="participants"
                          control={control}
                          render={({ field }) => (
                            <input type="hidden" {...field} />
                          )}
                        />
                      </FormGroup>
                    </Col>

                  </Row>

                  {/* Group Date */}
                  <Row>
                    <Col>
                      <FormGroup>
                        <H5 className="f-w-600 mb-2">Group Date</H5>
                        <Controller
                          name="date"
                          control={control}
                          rules={{ required: 'Date is required' }}
                          render={({ field }) => (
                            <Input
                              className="form-control"
                              type="date"
                              {...field}
                            />
                          )}
                        />
                        {errors.date && (
                          <span style={{ color: 'red' }}>{errors.date.message}</span>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Group Description */}
                  <Row>
                    <Col>
                      <FormGroup>
                        <H5 className="f-w-600 mb-2">Group Description</H5>
                        <Controller
                          name="description"
                          control={control}
                          rules={{ required: 'Description is required' }}
                          render={({ field }) => (
                            <Input
                              className="form-control"
                              type="textarea"
                              rows={3}
                              placeholder="Enter group description"
                              {...field}
                            />
                          )}
                        />
                        {errors.description && (
                          <span style={{ color: 'red' }}>{errors.description.message}</span>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Buttons */}
                  <Row>
                    <Col>
                      <div className="text-end">
                        <Btn
                          type="submit"
                          color="success"
                          className="me-3"
                          disabled={creating || updating}
                        >
                          {creating || updating ? 'Saving...' : group ? 'Update' : 'Add'}
                        </Btn>
                        <Btn type="button" color="danger">
                          <Link className="text-light d-flex align-items-center" to={`${process.env.PUBLIC_URL}/group/group_list`}>
                            {'Cancel'}
                          </Link>
                        </Btn>
                      </div>
                    </Col>
                  </Row>
                </Form>
             
  );
}
